/**
 * Anreicherungs-Zustaende: EINE reine Abbildung `Datenlage -> Zustand -> Satz + Handlung`.
 *
 * Bauplan: docs/KONZEPT-RECHERCHE-SICHTBARKEIT.md § 4.2 ("Ein Zustand, ein Satz, eine moegliche
 * Handlung"), Lebenszyklus § 4.1 (Migration 221/222/223). Kriterium R0.7 in docs/LOOP-RECHERCHE.md.
 *
 * ── Warum ein eigenes Paket ───────────────────────────────────────────────────────────────────
 * `apps/web` hat KEINEN Test-Runner (kein `test`-Skript, kein vitest, keine `*.test.tsx` — im
 * Beweis zu R0.2 ausdruecklich festgehalten). Ein Zustandsbaum in einer React-Komponente ist
 * damit unpruefbar: genau der Fall, in dem eine Anzeige leise zurueckfaellt in "Noch kein
 * Ergebnis", ohne dass ein Test es meldet. Dasselbe Muster wurde am 05.08.2026 fuer die
 * Deckungsregel etabliert (§ 5.9 / PR #419): die Regel wanderte in ein Paket, beide Seiten
 * importieren sie, eine Ratsche verhindert den Nachbau.
 *
 * NICHT `@maestro/pricing`: das Paket ist die einzige Wahrheit fuer BETRAEGE. Recherche-Daten sind
 * laut Invariante dieses Loops "Kontext fuer Text, nie Eingabe fuer Betraege" — sie in die
 * Preis-Engine zu legen, wuerde genau die Grenze verwischen, die der Loop schuetzt.
 * NICHT `@maestro/db`: das Paket zieht Drizzle mit; hier soll ausdruecklich NICHTS ausser
 * Standard-JavaScript stehen, damit `apps/web` es in den Browser-Bundle nehmen kann.
 *
 * Dieses Paket hat bewusst KEIN `test`-Skript: seine Tests liegen in `apps/api/test/`
 * (`anreicherung-zustand.test.ts`), weil genau diese Suite garantiert in der CI laeuft — dieselbe
 * Ueberlegung, mit der `nullpreis-deckung-eine-regel.test.ts` und `ci-testabdeckung.test.ts` dort
 * wohnen. Ein neues CI-Ziel waere damit nur zusaetzliche Mechanik ohne zusaetzliche Sicherheit.
 *
 * ── Reinheit ─────────────────────────────────────────────────────────────────────────────────
 * Kein `new Date()` ohne Argument, kein Zugriff auf `Intl`-Voreinstellungen ohne Ausweg: `jetzt`
 * und `zeitzone` sind Parameter. Ohne `zeitzone` formatiert `Intl` in der Zone der Laufzeit — im
 * Browser also in der Zone des Betrachters (das ist die Vorgabe: "naechster Versuch gegen HH:MM"
 * darf nicht in UTC stehen), im Test in der uebergebenen Zone (deterministisch).
 */

import { OHNE_DATUM, standDatum } from "@maestro/recherche-stand";

/* ────────────────────────────── Eingabe ────────────────────────────────────────────────────── */

/**
 * Eine Zeile aus `handelsregister_lookups` / `google_places_lookups`, so wie die API sie als JSON
 * ausliefert (Zeitstempel als ISO-Strings). Alle Lebenszyklus-Felder sind optional UND nullbar:
 * jede Zeile, die vor Migration 221 entstanden ist, traegt dort NULL (790 Bestandszeilen, im
 * Beweis zu R0.3 gemessen). Kein Zustand darf daraus "veraltet" oder "ausgesetzt" folgern.
 */
export interface AnreicherungsZeile {
  /** 'pending' | 'found' | 'ambiguous' | 'not_found' | 'error' | 'skipped' (Migration 221). */
  status: string;
  /** Maschinenlesbarer Grund, z.B. 'rate_limited' (Migration 222). NULL bei Bestandszeilen. */
  errorCode?: string | null;
  /** Klartext-Fehlermeldung der Quelle. */
  errorMessage?: string | null;
  /** Wann zuletzt tatsaechlich extern gefragt wurde (NULL = nie). */
  lookedUpAt?: string | null;
  /** Ab wann der Stand als veraltet gilt. NULL = keine TTL hinterlegt (NICHT "abgelaufen"). */
  validUntil?: string | null;
  /** Wann der naechste Versuch fruehestens laufen darf. */
  nextRetryAt?: string | null;
  /** Anlage bzw. letzte Wiederfreigabe der Zeile — Bezugspunkt fuer "haengt". */
  createdAt: string;
  /** Anzahl Treffer (matches.length) — nur fuer 'ambiguous' relevant. */
  trefferAnzahl?: number;
  /**
   * Migration 245: wann ein Mensch entschieden hat, dass dieses Ergebnis NICHT zu dem Datensatz
   * gehoert (`status='rejected'`). NULL/fehlend bei jeder anderen Zeile.
   */
  verworfenAm?: string | null;
}

export interface AnreicherungsLage {
  /** Mandanten-Schalter des Moduls (z.B. `tenantSettings.handelsregisterEnabled`). */
  modulAktiv: boolean;
  /** Anzeigename des Moduls, exakt wie in der Modul-Kachel (`apps/web/src/pages/settings.tsx`). */
  modulName: string;
  /** Firmenname, nach dem gesucht wurde/wird. */
  firma: string;
  /** Die Lookup-Zeile — `null` heisst: es gibt gar keine Zeile ("nie gefragt"). */
  zeile: AnreicherungsZeile | null;
  /** Bezugszeitpunkt. Parameter, damit die Abbildung rein und testbar bleibt. */
  jetzt: Date;
  /** IANA-Zone fuer HH:MM / TT.MM.JJJJ. Fehlt sie, gilt die Zone der Laufzeit (= des Betrachters). */
  zeitzone?: string;
  /**
   * Ab wann ein 'pending' als haengend gilt. Vorgabe 15 Minuten — dieselbe Grenze, ab der
   * `claim_handelsregister_lookup` die Zeile wieder freigibt (packages/db/sql/223, Freigabegrund
   * (b)). Zwei verschiedene Zahlen wuerden bedeuten: die Oberflaeche nennt einen Abruf haengend,
   * waehrend das Schema ihn noch fuer laufend haelt (oder umgekehrt).
   */
  haengtNachMinuten?: number;
}

/* ────────────────────────────── Ausgabe ────────────────────────────────────────────────────── */

/**
 * Die neun unterscheidbaren Zustaende aus § 4.2 — plus `aktuell` als zehnter Fall: ein gueltiges,
 * frisches Ergebnis. `aktuell` traegt bewusst KEINEN Satz; dort sprechen die Daten selbst
 * (Rechtsform, Register, Sitz …). Die neun anderen tragen genau einen Satz.
 */
export const ANREICHERUNGS_ZUSTAENDE = [
  "modul_aus",
  "nie_gefragt",
  "laeuft",
  "haengt",
  "ausgesetzt",
  "kein_treffer",
  "mehrdeutig",
  // Migration 245: es LAG ein Ergebnis vor, und ein Mensch hat gesagt, dass es nicht zu diesem
  // Datensatz gehoert. Weder "gefunden" noch "nicht gefunden" noch "nie gefragt" — bis hierher
  // gab es fuer diese Auskunft keinen Ort, und deshalb auch keinen Weg, sie zu treffen.
  "verworfen",
  "fehler",
  "veraltet",
  "aktuell",
] as const;
export type AnreicherungsZustand = (typeof ANREICHERUNGS_ZUSTAENDE)[number];

/** Die neun Zustaende mit Satz (alles ausser `aktuell`) — Grundlage der Testabdeckung. */
export const ZUSTAENDE_MIT_SATZ: readonly AnreicherungsZustand[] = ANREICHERUNGS_ZUSTAENDE.filter(
  (z) => z !== "aktuell",
);

/**
 * Welche Handlung die Oberflaeche anbieten darf.
 *
 * `modul_kachel` = Sprung in die Modul-Kachel (§ 4.2, Zeile "Modul aus").
 *
 * `neu_abrufen` = die manuelle Notbremse aus § 4.3, Mechanismus 4 — seit R0.13 gibt es dafuer
 * einen Weg: `POST /api/anreicherung/handelsregister` (Anker: Kunde ODER Anfrage) stoesst den
 * Abruf durch DENSELBEN Deckel und dieselbe Rollenpruefung an wie der automatische Weg. Bis dahin
 * gab diese Abbildung fuer VIER Zustaende `null` zurueck, obwohl § 4.2 dort eine Handlung
 * vorsieht — nicht aus Nachlaessigkeit, sondern weil eine Schaltflaeche ohne Endpunkt derselbe
 * Fehler gewesen waere wie der Einheitssatz, den R0.7 abgeschafft hat.
 *
 * Angeboten wird `neu_abrufen` nur, wo der Claim ihn auch ANNEHMEN wuerde (Migration 223):
 * `nie_gefragt` (es gibt noch keine Zeile), `haengt` (`pending` aelter als 15 Minuten),
 * `fehler` (`status='error'`) und `veraltet` (`valid_until` abgelaufen). NICHT bei:
 *
 *  - `laeuft` — ein zweiter Anstoss waere sofort abgewiesen (Dedup) und der Knopf taete nichts;
 *  - `ausgesetzt` — der Deckel ist gerade erreicht, der Nachversuch steht schon terminiert;
 *  - `aktuell` — ein gueltiges Ergebnis bleibt gesperrt, das ist der Zweck der TTL;
 *  - `laeuft`, `ausgesetzt`, `aktuell` (siehe oben);
 *  - `mehrdeutig` — dort steht keine EINZELNE Handlung, sondern eine Liste: die Trefferauswahl.
 *    Sie rendern die Karten aus einer eigenen Regel (`trefferauswahl()` fuer Places,
 *    `registerTrefferauswahl()` fuer das Handelsregister), weil eine Auswahl aus N Eintraegen
 *    nicht in ein Feld „eine Handlung" passt. Seit Migration 245 gibt es sie fuer BEIDE Quellen —
 *    die frueher hier vermerkte Schieflage („nur Places hat eine Auswahl") ist damit weg.
 *
 * `firma_bearbeiten` = „Anderen Namen versuchen" (§ 4.2, Zeile „kein Treffer"). Die Handlung
 * stand dort von Anfang an im Konzept und hatte bis Migration 245 kein Ziel: `apps/web` kannte
 * nur „Kunde anlegen", kein Formular zum Aendern eines BESTEHENDEN Kunden. Genau deshalb blieb
 * `kein_treffer` handlungslos — nicht, weil es nichts zu tun gaebe, sondern weil es keinen Weg
 * gab. Denselben Namen erneut abzufragen waere keine Loesung (gleicher Deckel, gleiche Antwort);
 * die Loesung ist ein anderer Name, und der aendert zugleich den normalisierten Schluessel, unter
 * dem die naechste Zeile entsteht.
 *
 * Angeboten wird `firma_bearbeiten` bei `kein_treffer` und bei `verworfen` — den beiden
 * Zustaenden, in denen feststeht, dass unter DIESEM Namen nichts (mehr) zu holen ist.
 *
 * Die Regel bleibt: keine Schaltflaeche ohne Wirkung.
 */
export type HandlungsArt = "modul_kachel" | "neu_abrufen" | "firma_bearbeiten";

export interface AnreicherungsHandlung {
  art: HandlungsArt;
  beschriftung: string;
}

/**
 * Die Beschriftungen des Neuabrufs — je Zustand die aus § 4.2, nicht eine fuer alle.
 *
 * „Jetzt abrufen" bei etwas, das schon einmal fehlgeschlagen ist, waere so falsch wie „Erneut
 * versuchen" bei etwas, das nie lief: der Knopftext ist die einzige Stelle, an der der Betreiber
 * erfaehrt, ob er gerade etwas WIEDERHOLT oder zum ERSTEN Mal anstoesst. Eine Art, vier Texte.
 */
const NEU_ABRUFEN = {
  nie_gefragt: { art: "neu_abrufen", beschriftung: "Jetzt abrufen" },
  haengt: { art: "neu_abrufen", beschriftung: "Erneut versuchen" },
  fehler: { art: "neu_abrufen", beschriftung: "Erneut versuchen" },
  veraltet: { art: "neu_abrufen", beschriftung: "Neu abrufen" },
} as const satisfies Record<string, AnreicherungsHandlung>;

/**
 * Der Wortlaut aus § 4.2, unveraendert uebernommen. Ein Text fuer beide Zustaende, weil die
 * Auskunft dieselbe ist: unter diesem Namen fuehrt der Weg nicht weiter.
 */
const FIRMA_BEARBEITEN = { art: "firma_bearbeiten", beschriftung: "Anderen Namen versuchen" } as const satisfies AnreicherungsHandlung;

export interface AnreicherungsAnzeige {
  zustand: AnreicherungsZustand;
  /** Genau ein Satz — `null` nur bei `aktuell`. */
  satz: string | null;
  /** Genau eine Handlung — `null`, wenn § 4.2 keine vorsieht ODER es keinen Weg dafuer gibt. */
  handlung: AnreicherungsHandlung | null;
}

/* ────────────────────────────── Die Trefferauswahl, quellenneutral ─────────────────────────── */

/**
 * Ein Eintrag einer Trefferauswahl — die Form, in der JEDE Quelle ihre Mehrdeutigkeit vorlegt.
 *
 * ── Warum die Form hier wohnt und nicht bei der Quelle ───────────────────────────────────────
 * Google Places hat die Auswahl seit R3.3 (`@maestro/places-anzeige`), das Handelsregister seit
 * Migration 245 (`@maestro/firmenprofil`). Beide beantworten dieselbe Frage („welcher von
 * mehreren ist gemeint?"), beide werden an derselben Stelle gerendert, und beide muessen sich
 * gleich anfuehlen. Zwei getrennte Formen waeren zwei Flaechen fuer eine Entscheidung — und die
 * zweite waere die schlechtere Kopie der ersten.
 *
 * Dieses Paket ist der richtige Ort, weil es das einzige quellenneutrale der Recherche ist: hier
 * steht schon die Zustandsabbildung, die fuer Handelsregister UND Places gilt. Beide Regel-Pakete
 * haengen ohnehin daran (`@maestro/firmenprofil` direkt, `@maestro/places-anzeige` seit 245).
 */
export interface TrefferEintrag {
  /** Der Wert, den die Auswahl an die API zurueckgibt (Places: `place_id`; Register: der Index). */
  readonly id: string;
  /** Fett darueber: der Name der Quelle — oder ein ehrlicher Ersatz, nie ein leerer Titel. */
  readonly titel: string;
  /** Die Zeilen darunter, ohne Leereintraege: genau die Angaben, an denen man unterscheidet. */
  readonly zeilen: readonly string[];
  /** Ein Hinweis zu GENAU diesem Eintrag (z.B. „dauerhaft geschlossen"), sonst `null`. */
  readonly warnung: string | null;
}

/** Die Auswahl selbst: ein Satz, eine Herkunftszeile (R1.6), die Eintraege. */
export interface Trefferliste {
  readonly satz: string;
  readonly herkunft: string;
  readonly eintraege: readonly TrefferEintrag[];
}

/**
 * Der Satz ueber jeder Trefferliste — EIN Wortlaut fuer alle Quellen, mit dem Wort, das die
 * Quelle fuer ihre Einheit benutzt („Betrieb" bei Google, „Eintrag" im Register).
 *
 * Der zweite Halbsatz ist die eigentliche Auskunft, nicht die Zahl: bis zur Antwort ist NICHTS
 * passiert. Genau das war der Befund B6 — vorher wurde stillschweigend der erste genommen.
 */
export function trefferauswahlSatz(anzahl: number, einheit: string): string {
  return `${anzahl} mögliche Treffer — bitte den richtigen ${einheit} auswählen. Bis dahin wurde nichts übernommen.`;
}

/* ────────────────────────────── Formatierung ───────────────────────────────────────────────── */

/** "14:05" in der Zone des Betrachters (bzw. der uebergebenen Zone). */
function uhrzeit(iso: string, zeitzone?: string): string {
  return new Date(iso).toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: zeitzone,
  });
}

/* Das Tagesdatum formatiert `standDatum` aus `@maestro/recherche-stand` (R1.6) — dieselbe
 * Funktion, die auch die Herkunftszeilen fuellt. Eine zweite lokale Fassung waere die zweite
 * Meinung darueber, was ohne Zeitstempel passiert. */

/** Millisekunden aus einem ISO-String — `null`, wenn er fehlt oder unlesbar ist. */
function alsZeit(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

/* ────────────────────────────── Die Abbildung ──────────────────────────────────────────────── */

/**
 * Bildet die Datenlage auf genau einen Zustand, genau einen Satz und hoechstens eine Handlung ab.
 *
 * Reihenfolge der Pruefungen (jede Zeile hat einen Grund):
 *  1. Modul aus schlaegt alles — ein ausgeschaltetes Modul erklaert JEDE andere Beobachtung, und
 *     die Handlung ("einschalten") ist die einzige, die weiterfuehrt.
 *  2. Keine Zeile = nie gefragt. Auf Produktion ist das der Regelfall: fuer keine der 27 Firmen
 *     existiert eine Zeile (Befund in docs/LOOP-RECHERCHE.md) — bisher stand dort "Noch kein
 *     Ergebnis … der Abruf laeuft einmalig im Hintergrund", was schlicht nicht stimmte.
 *  3. 'skipped' vor allem anderen, was einen Abruf behauptet: die Zeile sagt gerade aus, dass
 *     KEIN Abruf lief.
 *  4./5. 'pending' zerfaellt in laufend und haengend, Grenze wie im Schema (223).
 *  6. 'ambiguous' vor der Veralterung: mehrdeutig ist kein alterndes Ergebnis, sondern eine
 *     offene Rueckfrage.
 *  7. Veraltet gilt nur fuer ECHTE Ergebnisse ('found'/'not_found') mit gesetzter, abgelaufener
 *     TTL. NULL ist kein Beleg fuer Veralterung (dieselbe Lesart wie in Migration 223).
 */
export function anreicherungsAnzeige(lage: AnreicherungsLage): AnreicherungsAnzeige {
  const { modulAktiv, modulName, firma, zeile, jetzt, zeitzone } = lage;
  const haengtNachMinuten = lage.haengtNachMinuten ?? 15;

  // 1. Modul aus.
  if (!modulAktiv) {
    return {
      zustand: "modul_aus",
      satz: `Modul „${modulName}" ist ausgeschaltet.`,
      handlung: { art: "modul_kachel", beschriftung: "Zur Modul-Kachel" },
    };
  }

  // 2. Nie gefragt.
  if (!zeile) {
    return { zustand: "nie_gefragt", satz: "Noch nicht abgerufen.", handlung: NEU_ABRUFEN.nie_gefragt };
  }

  // 3. Bewusst nicht gefragt (Deckel, Migration 222).
  if (zeile.status === "skipped") {
    // Der Satz wird zusammengesetzt, damit er nie mehr behauptet, als die Zeile hergibt:
    // 'rate_limited' ist heute der einzige Grund, der geschrieben wird (222), und `next_retry_at`
    // kann bei einer Bestandszeile fehlen. Mit beiden Angaben steht dort exakt der Satz aus § 4.2.
    const grund = zeile.errorCode === "rate_limited" ? " (Stundenlimit erreicht)" : "";
    const retry = zeile.nextRetryAt ? ` — nächster Versuch gegen ${uhrzeit(zeile.nextRetryAt, zeitzone)}` : "";
    return { zustand: "ausgesetzt", satz: `Zurzeit ausgesetzt${grund}${retry}.`, handlung: null };
  }

  // 4./5. Laeuft vs. haengt.
  if (zeile.status === "pending") {
    const seit = alsZeit(zeile.createdAt);
    const haengt = seit !== null && jetzt.getTime() - seit >= haengtNachMinuten * 60_000;
    if (haengt) {
      return {
        zustand: "haengt",
        satz: `Abruf hängt seit ${uhrzeit(zeile.createdAt, zeitzone)}.`,
        handlung: NEU_ABRUFEN.haengt,
      };
    }
    return { zustand: "laeuft", satz: "Wird gerade abgerufen …", handlung: null };
  }

  // 6. Mehrdeutig.
  if (zeile.status === "ambiguous") {
    const n = zeile.trefferAnzahl ?? 0;
    return { zustand: "mehrdeutig", satz: `${n} mögliche Treffer — bitte auswählen.`, handlung: null };
  }

  // 6a. Verworfen (Migration 245) — VOR der Veralterung, aus demselben Grund wie 'ambiguous':
  // eine getroffene Entscheidung altert nicht, sie gilt. Der Satz nennt das Datum, weil ohne ihn
  // nicht erkennbar waere, ob die Entscheidung von heute oder von vorletztem Jahr stammt; ohne
  // Zeitstempel steht dort die Tatsache ohne Datum, nie ein erfundenes (dieselbe Linie wie R1.6).
  if (zeile.status === "rejected") {
    const stand = standDatum(zeile.verworfenAm, zeitzone);
    return {
      zustand: "verworfen",
      // Beschreibend und quellenneutral: derselbe Satz muss ueber einem Registerergebnis und
      // ueber einem Google-Vorschlag stehen koennen, und er sagt nur, was geschehen ist — kein
      // Urteil ueber die Firma, keine Zusage darueber, was als Naechstes passiert.
      satz: stand
        ? `Am ${stand} als unpassend vermerkt — übernommen wurde nichts.`
        : "Als unpassend vermerkt — übernommen wurde nichts.",
      handlung: FIRMA_BEARBEITEN,
    };
  }

  // 8. Fehler mit Klartext.
  if (zeile.status === "error") {
    const klartext = zeile.errorMessage?.trim();
    return {
      zustand: "fehler",
      // Ohne Klartext bleibt der Satz stehen, aber ohne den Doppelpunkt-Teil: eine leere Erklaerung
      // hinter einem Doppelpunkt liest sich wie eine verlorene Meldung.
      satz: klartext ? `Abruf fehlgeschlagen: ${klartext}` : "Abruf fehlgeschlagen.",
      handlung: NEU_ABRUFEN.fehler,
    };
  }

  // 7. Veraltet — nur bei echten Ergebnissen und nur mit gesetzter, abgelaufener TTL.
  if (zeile.status === "found" || zeile.status === "not_found") {
    const bis = alsZeit(zeile.validUntil);
    if (bis !== null && bis < jetzt.getTime()) {
      // R1.6: KEIN Rueckfall mehr auf `created_at`. Bis hierher stand dort
      // `zeile.lookedUpAt ?? zeile.createdAt` — die Anlage der Zeile als „Stand vom …" ausgegeben,
      // also ein plausibel aussehendes Falschdatum an genau der Stelle, an der die Anzeige den
      // Betrachter vor einem alten Stand warnen will. Fehlt `looked_up_at`, sagt der Satz das.
      const stand = standDatum(zeile.lookedUpAt, zeitzone);
      const kopf = stand ? `Stand vom ${stand}` : OHNE_DATUM.charAt(0).toUpperCase() + OHNE_DATUM.slice(1);
      return {
        zustand: "veraltet",
        satz: `${kopf} — möglicherweise veraltet.`,
        handlung: NEU_ABRUFEN.veraltet,
      };
    }
    if (zeile.status === "not_found") {
      // Seit Migration 245 traegt dieser Zustand die Handlung, die § 4.2 fuer ihn vorsieht:
      // „Anderen Namen versuchen". Sie hatte bis dahin kein Ziel (siehe HandlungsArt oben).
      return { zustand: "kein_treffer", satz: `Kein Eintrag zu „${firma}" gefunden.`, handlung: FIRMA_BEARBEITEN };
    }
    // 'found' innerhalb der TTL: die Daten selbst sind die Aussage.
    return { zustand: "aktuell", satz: null, handlung: null };
  }

  // Verteidigung gegen einen Status, den diese Abbildung nicht kennt (neuer Wert im Schema, ohne
  // dass diese Datei nachgezogen wurde). Bewusst NICHT stillschweigend als "aktuell" behandeln:
  // ein unlesbarer Zustand ist naeher an einem Fehler als an einem Ergebnis.
  //
  // Und bewusst OHNE "Erneut versuchen", anders als beim echten `status='error'`: ob der Claim
  // eine Zeile mit unbekanntem Status wieder freigibt, weiss hier niemand — die Freigabegruende in
  // Migration 223 sind namentlich aufgezaehlt. Ein Knopf, der vermutlich nichts tut, ist derselbe
  // Fehler wie ein Satz, der etwas Falsches behauptet.
  return { zustand: "fehler", satz: "Abruf fehlgeschlagen.", handlung: null };
}
